import React, { useState, useEffect } from 'react';
import { biometricService } from '../../../services/biometricService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

export default function BiometricPanel({ onRefresh }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isPlatformAvailable, setIsPlatformAvailable] = useState(false);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    checkSupport();
    loadMethods();
  }, []);

  const checkSupport = async () => {
    const supported = biometricService?.isSupported();
    setIsSupported(supported);
    
    if (supported) {
      const available = await biometricService?.isPlatformAuthenticatorAvailable();
      setIsPlatformAvailable(available);
    }
  };

  const loadMethods = async () => {
    setLoading(true);
    try {
      const data = await biometricService?.getBiometricMethods();
      setMethods(data);
    } catch (err) {
      console.error('Failed to load biometric methods:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!deviceName?.trim()) {
      setError('Please enter a device name');
      return;
    }

    setRegistering(true);
    setError('');

    try {
      await biometricService?.registerBiometric(deviceName?.trim());
      setDeviceName('');
      setShowAddForm(false);
      await loadMethods();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err?.message || 'Failed to register biometric authentication');
    } finally {
      setRegistering(false);
    }
  };

  const handleRemove = async (methodId) => {
    if (!window.confirm('Are you sure you want to remove this biometric authentication method?')) {
      return;
    }

    try {
      await biometricService?.removeBiometric(methodId);
      await loadMethods();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err?.message || 'Failed to remove biometric method');
    }
  };

  const getBiometricIcon = () => {
    const userAgent = navigator?.userAgent?.toLowerCase();
    if (userAgent?.includes('iphone') || userAgent?.includes('ipad')) {
      return 'Fingerprint'; // Face ID or Touch ID
    } else if (userAgent?.includes('android')) {
      return 'Fingerprint'; // Fingerprint or Face unlock
    } else if (userAgent?.includes('windows')) {
      return 'Scan'; // Windows Hello
    }
    return 'Shield';
  };

  if (!isSupported) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-gray-100 p-2 rounded-lg">
            <Icon name="ShieldOff" size={24} className="text-gray-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Biometric Authentication</h2>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            Biometric authentication is not supported on this browser. Please use a modern browser with WebAuthn support.
          </p>
        </div>
      </div>
    );
  }

  if (!isPlatformAvailable) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon name={getBiometricIcon()} size={24} className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Biometric Authentication</h2>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">
            Platform biometric authentication is not available on this device. This feature works best on mobile devices with fingerprint or face recognition.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 p-2 rounded-lg">
            <Icon name={getBiometricIcon()} size={24} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Biometric Authentication</h2>
            <p className="text-sm text-gray-600">Use fingerprint or face recognition</p>
          </div>
        </div>
        {!showAddForm && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setShowAddForm(true)}
          >
            Add Biometric
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {showAddForm && (
        <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Register Biometric Authentication</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device Name
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e?.target?.value)}
                placeholder="e.g., iPhone 15, Samsung Galaxy S24"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleRegister}
                loading={registering}
                disabled={registering || !deviceName?.trim()}
                iconName="Fingerprint"
                size="sm"
              >
                {registering ? 'Registering...' : 'Register Biometric'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  setDeviceName('');
                  setError('');
                }}
                disabled={registering}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : methods?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Fingerprint" size={48} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No biometric methods registered</p>
          <p className="text-sm text-gray-500">Add biometric authentication for quick and secure access</p>
        </div>
      ) : (
        <div className="space-y-3">
          {methods?.map((method) => (
            <div
              key={method?.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-3">
                <Icon name={getBiometricIcon()} size={20} className="text-indigo-600" />
                <div>
                  <p className="font-medium text-gray-900">{method?.deviceName}</p>
                  <p className="text-sm text-gray-600">
                    {method?.lastUsedAt
                      ? `Last used: ${new Date(method?.lastUsedAt)?.toLocaleDateString()}`
                      : 'Never used'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  method?.isEnabled
                    ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                }`}>
                  {method?.isEnabled ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => handleRemove(method?.id)}
                  className="text-red-600 hover:text-red-700 p-1"
                  title="Remove"
                >
                  <Icon name="Trash2" size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Mobile-Ready Biometric Authentication</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Works with fingerprint sensors and face recognition</li>
              <li>Credentials stored securely on your device</li>
              <li>Fast and convenient authentication</li>
              <li>Compatible with iOS Face ID, Touch ID, and Android biometrics</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}